import { Controller } from '@hotwired/stimulus'
import type { App } from 'vue'

import { createApp } from '@app/adapters/create-app'
import { getOverviewFilter } from '@app/adapters/silverfin'
import SFetch from '@app/utils/sfetch'
import { SfLog } from '@app/adapters/logger'
import ChecksNotVisibleBanner from '@app/application/workflows/components/checks-not-visible-banner.vue'
import type { Check, Checklist } from '@app/application/workflows/types'

interface ChecksNotVisibleResponse {
  checksNotVisibleInWorkflow: Check[]
  checklists: Checklist[]
}

// Mounts the "checks not visible in workflow" warning while the workflow's
// `checks` filter is active: connect() wires the workflows-filter eventHub
// listeners (and mounts if that filter is already active); disconnect() removes
// them and unmounts the Vue app.
export default class extends Controller {
  static values = { url: String, workflowId: Number }

  declare urlValue: string
  declare workflowIdValue: number
  private app?: App
  private abortController?: AbortController

  connect(): void {
    if (getOverviewFilter() === 'checks') {
      void this.initializeWarningMessage()
    }

    window.eventHub.$on('workflows-filter-reset', this.destroyWarningMessage)
    window.eventHub.$on(
      'workflows-filter-unselected',
      this.destroyWarningMessage
    )
    window.eventHub.$on('workflows-filter-selected', this.onFilterSelect)
  }

  disconnect(): void {
    window.eventHub.$off('workflows-filter-reset', this.destroyWarningMessage)
    window.eventHub.$off(
      'workflows-filter-unselected',
      this.destroyWarningMessage
    )
    window.eventHub.$off('workflows-filter-selected', this.onFilterSelect)
    this.destroyWarningMessage()
  }

  private initializeWarningMessage = async (): Promise<void> => {
    // Re-selecting `checks` should not stack a second mount, and aborts any
    // fetch still in flight from a previous selection.
    this.destroyWarningMessage()

    const { signal } = (this.abortController = new AbortController())

    try {
      const data = await SFetch.json<ChecksNotVisibleResponse>(this.urlValue, {
        signal,
      })

      if (data && data.checksNotVisibleInWorkflow.length > 0) {
        this.app = createApp(ChecksNotVisibleBanner, {
          checks: data.checksNotVisibleInWorkflow,
          checklists: data.checklists,
          workflowId: this.workflowIdValue,
        })
        this.app.mount(this.element)
      }
    } catch {
      // A teardown or newer request aborted this fetch while it was in flight;
      // that is expected, not a failure worth reporting.
      if (signal.aborted) return

      SfLog.warn(
        `Failed to fetch data for "checks not visible in workflow" in ${this.urlValue}`
      )
    }
  }

  private destroyWarningMessage = (): void => {
    // Abort any in-flight fetch so it can't mount after teardown.
    this.abortController?.abort()
    this.abortController = undefined
    this.app?.unmount()
    this.app = undefined
  }

  private onFilterSelect = (event: Event): void => {
    const target = (event.target as HTMLElement | null)?.closest<HTMLElement>(
      '[data-filter-status]'
    )

    if (!target) {
      this.destroyWarningMessage()
      return
    }

    if (target.dataset.filterStatus === 'checks') {
      void this.initializeWarningMessage()
    } else {
      this.destroyWarningMessage()
    }
  }
}
